import React, { useMemo } from "react";
import type { FormStepProps } from "../../types/application";
import { CityAutocomplete } from "../common/CityAutocomplete";
import { FormValidationSummary, useFormValidation } from "../common/FormValidationSummary";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import "../../assets/gcweb-theme.css";
import "../../assets/gcweb-font-fix.css";
import { isMobile } from "react-device-detect";
// Component for showing extracted data indicator
const ExtractedFieldIndicator: React.FC<{ isExtracted: boolean; t: any }> = ({
  isExtracted,
  t,
}) => {
  if (!isExtracted) return null;

  return (
    <span className="text-success small">
      <span
        className="glyphicon glyphicon-ok mrgn-rght-sm"
        aria-hidden="true"
      ></span>
      {t.extractedFromResume}
    </span>
  );
};

const ContactInfoEmploymentStep: React.FC<FormStepProps> = ({
  data,
  onUpdate,
  onNext,
  onPrevious,
}) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const fieldLabels = useMemo(
    () => ({
      firstName: t.firstNameLabel,
      lastName: t.lastNameLabel,
      email: t.emailLabel,
      citizenship: t.citizenshipLabel,
      currentLocation: t.currentLocationLabel,
    }),
    [t]
  );
  const validation = useFormValidation({ t, fieldLabels });

  const handleInputChange = (field: keyof typeof data, value: string) => {
    onUpdate({ [field]: value });
  };

  const handleEnhancedDataChange = (field: string, value: string) => {
    const enhancedData = { ...data.enhancedData, [field]: value };
    onUpdate({ enhancedData });
  };

  const handleSubmit = (e: React.FormEvent) => {
    validation.handleSubmit(e, onNext);
  };

  const enhancedData = data.enhancedData || {};
  const extractedFields = data.extractionMetadata?.extractedFields || [];
  const isFieldExtracted = (fieldName: string) =>
    extractedFields.includes(fieldName);

  const renderFieldHelp = (fieldId: string, helpText: string) => (
    <p id={`${fieldId}-help`} className="text-sm text-gray-700 mt-1 mb-2">
      {helpText}
    </p>
  );

  return (
    <div className="wb-frmvld">
      <form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        method="get"
        action="#"
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />

        {/* Section 1: Basic Contact Information */}
        <h3 className="text-lg font-semibold text-gray-900 mrgn-tp-0">
          {t.applicantDetails}
        </h3>

        <div className="space-y-6">
          {/* Name Fields */}
          <div className="mb-4">
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="firstName" className="required">
                    <span className="field-name">1. {t.firstNameLabel}</span>
                    <strong className="required"> {t.required}</strong>
                  </label>
                  {renderFieldHelp("firstName", t.firstNamePlaceholder)}
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="form-control"
                    value={data.firstName}
                    onChange={(e) => {
                      handleInputChange("firstName", e.target.value);
                    }}
                    aria-describedby="firstName-help"
                    required
                  />
                  <ExtractedFieldIndicator
                    isExtracted={isFieldExtracted("firstName")}
                    t={t}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="lastName" className="required">
                    <span className="field-name">2. {t.lastNameLabel}</span>
                    <strong className="required"> {t.required}</strong>
                  </label>
                  {renderFieldHelp("lastName", t.lastNamePlaceholder)}
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="form-control"
                    value={data.lastName}
                    onChange={(e) => {
                      handleInputChange("lastName", e.target.value);
                    }}
                    aria-describedby="lastName-help"
                    required
                  />
                  <ExtractedFieldIndicator
                    isExtracted={isFieldExtracted("lastName")}
                    t={t}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="mb-4">
            <div className="form-group">
              <label htmlFor="email" className="required">
                <span className="field-name">3. {t.emailLabel}</span>
                <strong className="required"> {t.required}</strong>
              </label>
              {renderFieldHelp("email", t.emailPlaceholder)}
              <input
                type="email"
                id="email"
                name="email"
                className="form-control max-w-2xl"
                value={data.email}
                onChange={(e) => {
                  handleInputChange("email", e.target.value);
                }}
                aria-describedby="email-help"
                required
              />
              <ExtractedFieldIndicator
                isExtracted={isFieldExtracted("email")}
                t={t}
              />
            </div>
          </div>

          {/* Citizenship */}
          <div className="mb-4">
            <div className="form-group">
              <label htmlFor="citizenship" className="required">
                <span className="field-name">4. {t.citizenshipLabel}</span>
                <strong className="required"> {t.required}</strong>
              </label>
              <select
                id="citizenship"
                name="citizenship"
                className="form-control"
                value={enhancedData.citizenship || ""}
                onChange={(e) => {
                  handleEnhancedDataChange("citizenship", e.target.value);
                }}
                required
              >
                <option value="">{t.citizenshipPlaceholder}</option>
                <option value="Canadian Citizen">{t.citizenshipCanadian}</option>
                <option value="Permanent Resident">{t.citizenshipPR}</option>
                <option value="Work Permit">{t.citizenshipWorkPermit}</option>
                <option value="Other">{t.citizenshipOther}</option>
              </select>
              <ExtractedFieldIndicator
                isExtracted={isFieldExtracted("citizenship")}
                t={t}
              />
            </div>
          </div>

          {/* Current Location */}
          <div className="mb-4">
            <div className="form-group">
              <label htmlFor="currentLocation" className="required">
                <span className="field-name">5. {t.currentLocationLabel}</span>
                <strong className="required"> {t.required}</strong>
              </label>
              {renderFieldHelp("currentLocation", t.currentLocationPlaceholder)}
              <CityAutocomplete
                id="currentLocation"
                value={enhancedData.currentLocation || ""}
                onChange={(value) => {
                  handleEnhancedDataChange("currentLocation", value);
                }}
                className="form-control w-full max-w-xs"
                placeholder=""
                ariaDescribedBy="currentLocation-help"
                required
              />
              <ExtractedFieldIndicator
                isExtracted={isFieldExtracted("currentLocation")}
                t={t}
              />
            </div>
          </div>
        </div>

        {/* Navigation */}
        {isMobile ? (
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onPrevious}
              className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.previousButton}
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
            >
              {t.nextButton}
            </button>
          </div>
        ) : (
          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.previousButton}
            </button>
            <button
              type="submit"
              className="px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
            >
              {t.nextButton}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactInfoEmploymentStep;
